import * as React from "react";
import { configureApi } from "@/lib/apiClientRuntime";
import { setApiScope } from "@/lib/apiPath";
import {
  BrandLocation,
  createPublicSession,
  fetchPublicCompanySettings,
} from "./publicSessionService";
import { widgetI18n } from "./lib/i18n/widgetI18n";
import {
  WIDGET_LANGUAGE_STORAGE_KEY,
  changeLanguageAndPersist,
  normalizeLanguageCode,
  readLanguageCookie,
  readQueryLanguage,
} from "@/lib/i18n/languageSettings";
import type { CompanySettings } from "@/lib/types/company";

function updateWidgetDocumentTitle(settings: CompanySettings): void {
  if (typeof document === "undefined") return;

  const title = [settings.widget_title, settings.public_title]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");

  if (title) {
    document.title = title;
  }
}

/** True when /public/sessions refused the session because the salon's
 *  subscription expired (docs/codebase/subscription/README.md §5):
 *  403 {"detail": {"code": "license_expired", ...}} */
function isLicenseExpiredError(e: unknown): boolean {
  const detail = (e as any)?.response?.data?.detail;
  return (e as any)?.response?.status === 403 && detail?.code === "license_expired";
}

/** Functional sibling locations shipped with a license_expired 403 —
 *  one lapsed branch never blocks the rest of the brand. */
function locationsFromError(e: unknown): BrandLocation[] {
  const detail = (e as any)?.response?.data?.detail;
  return Array.isArray(detail?.locations) ? detail.locations : [];
}

function locationStorageKey(widgetHost: string): string {
  return `boona:widget:location:${widgetHost}`;
}

function readQueryLocation(): number | null {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("location");
  const parsed = raw ? parseInt(raw, 10) : NaN;
  return Number.isFinite(parsed) ? parsed : null;
}

/** waitlist claim deep link (waitlist README §4.4 ⟲): bypasses the brand
 * location chooser — the entry's company is the link's widget host. */
export function readQueryClaim(): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("waitlist_claim");
}

/** return-reminder opt-out action link (return-reminders README §4.5 in
 * the backend repo): ?action=reminders_optout&token=… — same chooser
 * bypass as claims, the reminder's company is the link's widget host. */
export function readQueryRemindersOptout(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get("action") !== "reminders_optout") return null;
  return params.get("token");
}

/**
 * Widget bootstrap. Multi-salon brands (docs/codebase/multi-salon/ §6.5):
 * when /public/sessions returns locations[], the client must pick one before
 * the booking flow starts — unless a ?location= deep link or a remembered
 * choice already answers the question.
 */
export function usePublicBootstrap(opts: { apiBase?: string; widgetHost: string }) {
  const { apiBase = (import.meta.env.VITE_API_URL ?? "https://boona.app"), widgetHost } = opts;
  const [ready, setReady] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);
  const [licenseExpired, setLicenseExpired] = React.useState(false);
  const [companyId, setCompanyId] = React.useState<number | null>(null);
  const [locations, setLocations] = React.useState<BrandLocation[]>([]);
  const [needsLocationChoice, setNeedsLocationChoice] = React.useState(false);
  const [chosenCompanyId, setChosenCompanyId] = React.useState<number | null>(null);
  const [waitlist, setWaitlist] = React.useState<{ active: boolean; horizon_days: number } | null>(null);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const hasLanguageCookie = Boolean(readLanguageCookie());
        const queryLanguage = readQueryLanguage();

        // 1. Check local storage for cached region to optimize initial connection
        const cachedRegion = localStorage.getItem("widget_region");
        if (cachedRegion && cachedRegion !== "default") {
          configureApi(`https://api-${cachedRegion}.boona.app`);
        } else {
          configureApi(apiBase);
        }
        // Ensure public scope so apiPath('/company/settings') resolves to /api/v1/public/...
        setApiScope("public");

        // 2. Location preference: explicit pick > ?location= deep link >
        //    remembered choice from this browser session.
        let stored: number | null = null;
        try {
          const raw = sessionStorage.getItem(locationStorageKey(widgetHost));
          stored = raw ? parseInt(raw, 10) : null;
        } catch { /* storage unavailable — ignore */ }
        const target = chosenCompanyId ?? readQueryLocation() ?? stored;

        // 3. Create session: by picked company, else by widget_host
        const session = await createPublicSession(
          target ? { companyId: target } : { widgetHost },
        );
        const { companyId: resolvedCompanyId, region } = session;
        const brandLocations = session.locations ?? [];

        if (alive && session.waitlist?.active) setWaitlist(session.waitlist);

        // 3b. Multi-salon brand without a chosen location → show the chooser
        //     BEFORE the booking flow (README §6.5). A waitlist claim link
        //     bypasses it — the claimed entry lives on THIS host's company.
        if (!target && !readQueryClaim() && !readQueryRemindersOptout()
            && brandLocations.length > 1) {
          if (alive) {
            setLocations(brandLocations);
            setNeedsLocationChoice(true);
          }
          return;
        }

        // 4. Switch to regional API if backend returned a region
        if (region) {
          localStorage.setItem("widget_region", region);
          if (region !== "default") {
            configureApi(`https://api-${region}.boona.app`);
          }
        }

        // 5. Fetch company settings (now targeting the correct regional API)
        const settings = await fetchPublicCompanySettings();
        updateWidgetDocumentTitle(settings);

        if (!hasLanguageCookie) {
          if (queryLanguage) {
            await changeLanguageAndPersist(widgetI18n, queryLanguage, WIDGET_LANGUAGE_STORAGE_KEY);
          } else {
            const companyLanguage = normalizeLanguageCode(settings.locale_short);
            if (companyLanguage) {
              await changeLanguageAndPersist(widgetI18n, companyLanguage, WIDGET_LANGUAGE_STORAGE_KEY);
            }
          }
        }

        if (alive) {
          setNeedsLocationChoice(false);
          setCompanyId(resolvedCompanyId);
          setReady(true);
        }
      } catch (e) {
        if (alive) {
          if (isLicenseExpiredError(e)) {
            // Entered branch is expired — offer functional siblings if any
            const siblings = locationsFromError(e);
            if (siblings.length > 0) {
              setLocations(siblings);
              setNeedsLocationChoice(true);
            } else {
              setLicenseExpired(true);
            }
          } else {
            setError(e as Error);
          }
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [apiBase, widgetHost, chosenCompanyId]);

  const chooseLocation = React.useCallback((locationCompanyId: number) => {
    try {
      sessionStorage.setItem(
        locationStorageKey(widgetHost), String(locationCompanyId),
      );
    } catch { /* storage unavailable — choice just won't persist */ }
    setNeedsLocationChoice(false);
    setChosenCompanyId(locationCompanyId);  // re-runs the bootstrap effect
  }, [widgetHost]);

  return {
    ready, error, licenseExpired, companyId,
    locations, needsLocationChoice, chooseLocation,
    waitlist,
    claimToken: readQueryClaim(),
    remindersOptoutToken: readQueryRemindersOptout(),
  };
}
